import * as LaunchDarklySdk from 'launchdarkly-js-client-sdk'
import isEqual from 'lodash.isequal'
import { produce } from 'immer'
import { log } from '@egw/logger'
import { configClient } from '@egw/config'
import { type TAppConfig, type EMultiContextEnum, type TLaunchDarklyState, type TMultiContextType } from '../../types'
import { buildAppConfigContext, contextBuilderMap } from '../../utils'
import { type Config } from '../../config'

// when we first start, we don't know who the user is
const anonymousUserContext = {
  kind: 'user',
  anonymous: true,
}

// we we first start, our multi context only has an anonymous user
const initialMultiContext = {
  kind: 'multi',
  user: anonymousUserContext,
}

const multiContextInit: TMultiContextType = {
  kind: 'multi',
  user: anonymousUserContext,
  device: undefined,
  platform: undefined,
  ipLocation: undefined,
  userSource: undefined,
  appConfig: undefined,
  // enrollApplication: null,
}

// const log = logger('LaunchDarkly Service')

let instance: LaunchDarklyClient | null = null

export class LaunchDarklyClient {
  private client?: LaunchDarklySdk.LDClient
  public state: TLaunchDarklyState = {
    clientReady: false,
    initialized: false,
    failed: false,
    multiContext: multiContextInit,
    allFlags: {},
  }

  constructor() {
    if (!instance) {
      instance = this
    }
  }

  setClient(client: LaunchDarklySdk.LDClient): void {
    this.client = client
  }

  getClient(): LaunchDarklySdk.LDClient | undefined {
    return this.client
  }

  setState(mutationFn: (state: TLaunchDarklyState) => void): void {
    this.state = produce(this.state, mutationFn)
  }

  getState(): TLaunchDarklyState {
    return this.state
  }
}

const launchDarklyClientInstance = new LaunchDarklyClient()

const initializeClient = () => {
  if (typeof window === 'undefined') return null
  const config = configClient.getConfig() as Config

  const client = LaunchDarklySdk.initialize(config.launchDarkly.clientSideId, initialMultiContext)

  client.on('failed', () => {
    //client encountered an error that prevented connection to LaunchDarkly.  All flags receive default values.
    log.debug('client failed')
    launchDarklyClientInstance.setState((state) => {
      state.failed = true
    })
  })

  client.on('initialized', () => {
    // client has successfully started up and has valid feature flag data
    log.debug('client initialized')
    launchDarklyClientInstance.setState((state) => {
      state.initialized = true
    })
  })

  client.on('change', (flagChangeset: Record<any, any>) => {
    // the client has received new feature flag data.  This can happen either because contexts have been switched
    // using identify(), or client has a stream connection and received a live change to a flag value
    log.debug('client change -> ', flagChangeset)
    // getFlag method will not work here during Unit Tests. For Testing purposes retrieving flags w/ available client.
    const allFlags = client.allFlags()
    launchDarklyClientInstance.setState((state) => {
      state.allFlags = allFlags
    })
    log.debug('updated allFlags => ', allFlags)
  })

  client.on('ready', () => {
    // client has finished startup up.  Will be sent whether it connected successfully, or encountered an error
    log.debug('client ready')
    // getFlag method will not work here during Unit Tests. For Testing purposes retrieving flags w/ available client.
    const allFlags = client.allFlags()
    launchDarklyClientInstance.setState((state) => {
      state.allFlags = allFlags
      state.clientReady = true
    })
  })

  return client
}

const getLaunchDarklyClient = () => {
  const launchDarklyClient = launchDarklyClientInstance.getClient()
  if (!launchDarklyClient) {
    const client = initializeClient()
    if (client) {
      launchDarklyClientInstance.setClient(client)
    }
    return client
  }
  return launchDarklyClient
}

const updateMultiContext = async ({ key, args }: { key: EMultiContextEnum; args: unknown }) => {
  const launchDarklyClient = getLaunchDarklyClient()
  if (!launchDarklyClient) return
  // should spread new details into existing context
  // if new + existing context has all values needed update the context
  const newContext = contextBuilderMap({ key, args })
  const state = launchDarklyClientInstance.getState()
  // builds new context
  const updatedContext = {
    ...state.multiContext,
    [key]: newContext,
  }

  const contextsAreEqual = isEqual(state.multiContext, updatedContext)

  log.debug('update multi context ->', { contextsAreEqual, currentContext: state.multiContext, updatedContext })
  // should check contexst is different
  if (contextsAreEqual) return

  log.debug('context to be updated with -> ', newContext)

  // set updated context into state
  launchDarklyClientInstance.setState((state) => {
    state.multiContext = updatedContext
  })

  // TODO: IF NEEDED IMPLEMENT A CONTEXT CHECK HERE
  // Set new Context w/ Launch Darkly
  await launchDarklyClient
    .identify(updatedContext)
    .then(() => {
      log.debug('identity changed, allFlags => ', getAllFlags())
    })
    .catch((e: unknown) => {
      log.debug('error identifying user with mulitcontext', e)
    })
}

const getAllFlags = () => {
  const launchDarklyClient = getLaunchDarklyClient()
  if (launchDarklyClient) {
    const allFlags = launchDarklyClient.allFlags()
    launchDarklyClientInstance.setState((state) => {
      state.allFlags = allFlags
    })
    return allFlags
  }
  log.debug('Feature Client not initialized')
}

const hasFeature = (flag: string): LaunchDarklySdk.LDFlagValue => {
  const launchDarklyClient = getLaunchDarklyClient()
  if (launchDarklyClient) {
    return launchDarklyClient.variation(flag)
  }
  log.debug('Feature Client not initialized')
}

interface LaunchDarklyServiceInterface {
  hasFeature: (arg1: string) => boolean
  updateMultiContext: ({ key, args }: { key: EMultiContextEnum; args: unknown }) => Promise<void>
  getAllFlags: () => LaunchDarklySdk.LDFlagSet | undefined
  state: TLaunchDarklyState
  utils: {
    buildAppConfigContext: (appConfig: TAppConfig) => any
  }
}

export const launchDarklyService: LaunchDarklyServiceInterface = {
  /*
    Method that returns a boolean, based on if Client has the feature flag turned on.
    */
  hasFeature,

  /*
    Method that allows to update Launch Darkly Context. Context will be checked for completeness before updating Launch Darkly context.
    This allows for all providing services to initialize before invoking the context update.
    */
  updateMultiContext,

  /*
    Method that retrieves all the feature flags of the current User.
    */
  getAllFlags,

  /*
      multiContext: TMultiContextType;
      clientReady: boolean;
      failed: boolean;
      initialized: boolean;
      allFlags: Record<string, boolean>
    */
  // @deprecated - used only for testing purposes, may not work as expected
  state: launchDarklyClientInstance.getState(),

  /* Utilities / helpers */
  utils: {
    buildAppConfigContext,
  },
}
